import { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { motion, useReducedMotion } from 'framer-motion'
import {
  altColorDarkText,
  altColorLighter,
  helperTextColor,
  mainColorLighter,
} from 'src/ui/theme/tokens'
import { err, split } from 'src/utils/functions'
import {
  DiscoveryHeading,
  DiscoveryFilters,
  DiscoveryCard,
  DiscoveryLoading,
  DiscoveryEmpty,
  DiscoveryPagination,
} from '@components/common/Discovery'
import { ListItem as ListItemType, List as ListType } from '../api/types/lists'
import { getListCategories, getLists, getListTags, getMyLists } from 'src/api/lists'
export default function ListSearch({
  onListSelect,
  onStartGame,
  showMine,
  onShowMine,
  userId,
  onEditList,
  sounds,
}: {
  onListSelect: (id: number | undefined, prompt?: string, items?: ListItemType[]) => void
  onStartGame: (listId?: number) => void
  showMine?: boolean
  onShowMine?: (value: boolean) => void
  userId?: number
  onEditList?: (list: ListType) => void
  sounds?: { click?: () => void }
}): JSX.Element {
  const [lists, setLists] = useState<ListType[]>([])
  const [tags, setTags] = useState<string[] | undefined>()
  const [categories, setCategories] = useState<string[] | undefined>()

  const [listSearchLoading, setListSearchLoading] = useState<boolean>(true)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [listSearchLimit, setListSearchLimit] = useState<number>(10)
  const [listSearchTotal, setListSearchTotal] = useState<number>(0)
  const reducedMotion = useReducedMotion()

  const [listSearchQuery, setListSearchQuery] = useState<string>('')
  const [debouncedQuery, setDebouncedQuery] = useState<string>('')
  const [listSearchCategories, setListSearchCategories] = useState<string[]>([])
  const [listSearchTags, setListSearchTags] = useState<string[]>([])
  const [listSearchSort, setListSearchSort] = useState<'popular' | 'rating' | 'new' | ''>(
    showMine ? '' : 'popular'
  )

  const [listSearchPage, setListSearchPage] = useState<number>(0)

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [myListsSearchLimit, setMyListsSearchLimit] = useState<number>(10)
  // const [myListsSearchPage, setMyListsSearchPage] = useState<number>(0)
  const isMyLists = !!showMine

  const normalizeSearchToken = (token: string) => token.toLowerCase().replace(/[^a-z0-9]/g, '')
  const searchWords = debouncedQuery
    ? debouncedQuery
        .split(/\s+/)
        .map((word) => normalizeSearchToken(word))
        .filter(Boolean)
    : []

  const renderHighlightedTitle = (title: string) => {
    if (!searchWords.length) {
      return title
    }

    const tokens = title.split(/(\s+)/)
    const segments: { text: string; highlight: boolean }[] = []

    tokens.forEach((token) => {
      if (!token || /^\s+$/.test(token)) {
        segments.push({ text: token, highlight: false })
        return
      }
      const normalized = normalizeSearchToken(token)
      if (!normalized) {
        segments.push({ text: token, highlight: false })
        return
      }

      const ranges: Array<[number, number]> = []
      searchWords.forEach((word) => {
        if (!word) return
        let start = 0
        const lower = normalized.toLowerCase()
        const needle = word.toLowerCase()
        while (true) {
          const idx = lower.indexOf(needle, start)
          if (idx === -1) break
          ranges.push([idx, idx + needle.length])
          start = idx + needle.length
        }
      })

      if (!ranges.length) {
        segments.push({ text: token, highlight: false })
        return
      }

      ranges.sort((a, b) => a[0] - b[0])
      const merged: Array<[number, number]> = []
      ranges.forEach(([start, end]) => {
        const last = merged[merged.length - 1]
        if (!last || start > last[1]) {
          merged.push([start, end])
        } else {
          last[1] = Math.max(last[1], end)
        }
      })

      let cursor = 0
      merged.forEach(([start, end]) => {
        if (start > cursor) {
          segments.push({ text: token.slice(cursor, start), highlight: false })
        }
        segments.push({ text: token.slice(start, end), highlight: true })
        cursor = end
      })
      if (cursor < token.length) {
        segments.push({ text: token.slice(cursor), highlight: false })
      }
    })

    return segments.map((segment, idx) => (
      <Box
        component="span"
        key={`${segment.text}-${idx}`}
        sx={
          segment.highlight
            ? {
                backgroundColor: 'rgba(255, 245, 200, 1)',
                color: altColorDarkText,
                borderRadius: '24px',
                // px: 1,
                // py: 0.15,
              }
            : undefined
        }
      >
        {segment.text}
      </Box>
    ))
  }

  const trimMetaLabel = (value?: string) => (value || '').trim().slice(0, 16)
  const sortMetaBySelection = (values: string[], selected?: string[]) => {
    if (!selected?.length) return values
    const selectedSet = new Set(selected)
    return values.slice().sort((a, b) => {
      const aSel = selectedSet.has(a)
      const bSel = selectedSet.has(b)
      if (aSel === bSel) return 0
      return aSel ? -1 : 1
    })
  }

  const fetchLists = (opts?: {
    page?: number
    limit?: number
    search?: string
    tags?: string[]
    categories?: string[]
    sort?: string
  }) => {
    if (isMyLists) {
      return getMyLists({
        page: opts?.page,
        limit: opts?.limit,
        search: opts?.search,
        tags: opts?.tags,
        categories: opts?.categories,
        sort: opts?.sort,
      })
    }
    return getLists({
      page: opts?.page,
      limit: opts?.limit,
      search: opts?.search,
      tags: opts?.tags,
      categories: opts?.categories,
      sort: opts?.sort,
    })
  }

  useEffect(() => {
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    })
  }, [])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedQuery(listSearchQuery)
    }, 500)
    return () => window.clearTimeout(timer)
  }, [listSearchQuery])

  useEffect(() => {
    let cancelled = false
    setListSearchLoading(true)
    fetchLists({
      limit: listSearchLimit,
      page: listSearchPage,
      search: debouncedQuery,
      tags: listSearchTags,
      categories: listSearchCategories,
      sort: listSearchSort || undefined,
    })
      .then((res) => {
        if (cancelled) return
        setListSearchLoading(false)
        if (res.success) {
          setLists(res.data?.lists || [])
          setListSearchTotal(res.data?.total || 0)
        }
      })
      .catch((e) => {
        if (cancelled) return
        setListSearchLoading(false)
        err(e)
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    listSearchPage,
    listSearchLimit,
    listSearchTags,
    debouncedQuery,
    listSearchCategories,
    isMyLists,
    listSearchSort,
  ])

  useEffect(() => {
    if (!tags) {
      getListTags()
        .then((res) => {
          if (res.success && res.data?.length) {
            setTags(res.data)
          }
        })
        .catch((e) => err(e))
    }

    if (!categories) {
      getListCategories()
        .then((res) => {
          if (res.success && res.data?.length) {
            setCategories(res.data)
          }
        })
        .catch((e) => err(e))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSearchLists = async (
    opts: {
      tags?: string[]
      categories?: string[]
    } = {}
  ) => {
    onListSelect(undefined)
    setListSearchPage(0)
    if (opts?.tags) {
      setListSearchTags(opts.tags)
    }
    if (opts?.categories) {
      setListSearchCategories(opts.categories)
    }
    setDebouncedQuery(listSearchQuery)
  }

  const handleClearSearch = async () => {
    onListSelect(undefined)
    setListSearchCategories([])
    setListSearchTags([])
    setListSearchQuery('')
    setListSearchPage(0)
    setDebouncedQuery('')
    onShowMine?.(false)
  }

  return (
    <Box data-rt-tab-section sx={{ px: { xs: 1.5, sm: 3 }, pt: 1, pb: 2 }}>
      <DiscoveryFilters
        heading={
          <DiscoveryHeading
            title={isMyLists ? 'Your trivia collection' : 'Find your next challenge.'}
            description="Explore a world of things worth ranking."
          />
        }
        query={listSearchQuery}
        onQuery={(value) => {
          setListSearchQuery(value)
          setListSearchPage(0)
        }}
        categories={listSearchCategories}
        categoryOptions={categories || []}
        onCategories={(value) => {
          setListSearchCategories(value)
          setListSearchPage(0)
        }}
        tags={listSearchTags}
        tagOptions={tags || []}
        onTags={(value) => {
          setListSearchTags(value)
          setListSearchPage(0)
        }}
        onRemove={sounds?.click}
        filters={[
          {
            label: 'Popular',
            value: 'popular',
            selected: !isMyLists && listSearchSort === 'popular',
          },
          {
            label: 'By Rating',
            value: 'rating',
            selected: !isMyLists && listSearchSort === 'rating',
          },
          {
            label: 'New',
            value: 'new',
            selected: !isMyLists && listSearchSort === 'new',
          },
          ...(userId ? [{ label: 'My Trivia', value: 'mine', selected: isMyLists }] : []),
        ]}
        onFilter={(value) => {
          sounds?.click?.()
          setListSearchPage(0)
          if (value === 'mine') {
            onShowMine?.(!isMyLists)
            setListSearchSort('')
          } else {
            onShowMine?.(false)
            setListSearchSort(value as typeof listSearchSort)
          }
        }}
      />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1.5,
        }}
      >
        <Typography fontWeight={800} fontSize={14}>
          {isMyLists ? 'Made by you' : 'Ready to rank'}
        </Typography>
        <Typography variant="caption" color={helperTextColor} aria-live="polite">
          {listSearchLoading ? 'Finding trivia…' : listSearchTotal.toLocaleString() + ' results'}
        </Typography>
      </Box>
      {listSearchLoading ? (
        <DiscoveryLoading />
      ) : !lists.length ? (
        <DiscoveryEmpty
          onClear={() => {
            sounds?.click?.()
            void handleClearSearch()
          }}
        />
      ) : (
        <Box
          component={motion.div}
          key={JSON.stringify([
            listSearchPage,
            debouncedQuery,
            listSearchTags,
            listSearchCategories,
            listSearchSort,
            isMyLists,
          ])}
          initial={{ opacity: reducedMotion ? 1 : 0, y: reducedMotion ? 0 : 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.16 }}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 1.5,
          }}
        >
          {lists.map((list) => (
            <DiscoveryCard
              key={list.id}
              title={renderHighlightedTitle(list.prompt)}
              badge={list.private ? 'Private' : undefined}
              plays={list.plays || 0}
              rateUps={
                list.ratesUp ?? Math.max(0, Math.round((list.rating || 0) * (list.rates || 0)))
              }
              rateDowns={
                list.ratesDown ??
                Math.max(0, (list.rates || 0) - Math.round((list.rating || 0) * (list.rates || 0)))
              }
              metadata={[
                ...sortMetaBySelection(split(list.category, ','), listSearchCategories).map(
                  (value) => (
                    <Chip
                      key={'category-' + value}
                      size="small"
                      label={trimMetaLabel(value)}
                      onClick={() => {
                        sounds?.click?.()
                        void handleSearchLists({
                          categories: Array.from(new Set([...listSearchCategories, value])),
                        })
                      }}
                      sx={{
                        maxWidth: '100%',
                        background: mainColorLighter,
                        fontSize: 11,
                      }}
                    />
                  )
                ),
                ...sortMetaBySelection(split(list.tags, ','), listSearchTags).map((value) => (
                  <Chip
                    key={'tag-' + value}
                    size="small"
                    label={trimMetaLabel(value)}
                    onClick={() => {
                      sounds?.click?.()
                      void handleSearchLists({
                        tags: Array.from(new Set([...listSearchTags, value])),
                      })
                    }}
                    sx={{
                      maxWidth: '100%',
                      background: altColorLighter,
                      color: altColorDarkText,
                      fontSize: 11,
                    }}
                  />
                )),
              ]}
            >
              {!!userId && list.creatorId === userId && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    sounds?.click?.()
                    onEditList?.(list)
                  }}
                >
                  Edit
                </Button>
              )}
              <Button
                variant="contained"
                onClick={() => {
                  onListSelect(list.id, list.prompt, list.items || [])
                  setTimeout(() => onStartGame(list.id), 100)
                }}
              >
                Play
              </Button>
            </DiscoveryCard>
          ))}
        </Box>
      )}
      {!listSearchLoading && (
        <DiscoveryPagination
          page={listSearchPage}
          total={listSearchTotal}
          limit={listSearchLimit}
          onPage={(value) => {
            sounds?.click?.()
            setListSearchPage(value)
            window.scrollTo({ top: 0, behavior: 'smooth' })
          }}
        />
      )}
    </Box>
  )
}
